// Item lifecycle dates: when an item was first seen, when the source feed says
// it was updated, and when the daily fetch last detected a content change.
// See docs/operations.md ("Change detection") for how the daily flow fills
// clarito_lastchanged.

import type { ParsedItem } from './types';

const MS_PER_DAY = 86_400_000;

// A "Päivitetty" marker is shown when the last detected change is this recent.
export const CHANGED_MARKER_DAYS = 30;

// Returns the value when it is a non-empty string that parses as a date,
// otherwise null. Rows from before the column rollout simply lack the field.
export function dateStringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  if (!v) return null;
  return isNaN(new Date(v).getTime()) ? null : v;
}

// A lastChanged later than now + this is treated as bogus and ignored: it
// would otherwise pin the item to the top of every list and keep the
// "Päivitetty" marker on indefinitely. Up to a day absorbs clock skew.
export const FUTURE_CHANGE_TOLERANCE_MS = MS_PER_DAY;

function timeOrNaN(value: string | null | undefined): number {
  return value ? new Date(value).getTime() : NaN;
}

// lastChanged as a timestamp; NaN when missing, unparseable or more than
// FUTURE_CHANGE_TOLERANCE_MS in the future.
function changedTimeOrNaN(lastChanged: string | null | undefined, now: number): number {
  const t = timeOrNaN(lastChanged);
  return t > now + FUTURE_CHANGE_TOLERANCE_MS ? NaN : t;
}

// "Activity" time for newest-first ordering: the later of publishedDate and
// lastChanged (see changedTimeOrNaN). Falls back to whichever of the two
// parses; NaN when neither does.
export function activityTime(
  item: Pick<ParsedItem, 'publishedDate' | 'lastChanged'>,
  now: number = Date.now(),
): number {
  const pub = timeOrNaN(item.publishedDate);
  const changed = changedTimeOrNaN(item.lastChanged, now);
  if (isNaN(changed)) return pub;
  if (isNaN(pub)) return changed;
  return Math.max(pub, changed);
}

// The activityDate string: lastChanged when it is later than publishedDate
// (and not implausibly in the future), otherwise publishedDate (unchanged,
// may be '' for legacy rows).
export function activityDateOf(
  item: Pick<ParsedItem, 'publishedDate' | 'lastChanged'>,
  now: number = Date.now(),
): string {
  const pub = timeOrNaN(item.publishedDate);
  const changed = changedTimeOrNaN(item.lastChanged, now);
  if (!isNaN(changed) && (isNaN(pub) || changed > pub)) return item.lastChanged as string;
  return item.publishedDate;
}

// Newest activity first. Use for every "Uusin ensin" list ordering; timeline
// placement keeps using effectiveDate. Total order: items without a usable
// date sort last (as -Infinity), ties break by id ascending, so the order is
// deterministic whatever the input order.
export function compareActivityDesc(
  a: Pick<ParsedItem, 'id' | 'publishedDate' | 'lastChanged'>,
  b: Pick<ParsedItem, 'id' | 'publishedDate' | 'lastChanged'>,
): number {
  const now = Date.now();
  const ta = activityTime(a, now);
  const tb = activityTime(b, now);
  const na = isNaN(ta) ? -Infinity : ta;
  const nb = isNaN(tb) ? -Infinity : tb;
  if (na !== nb) return nb > na ? 1 : -1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

// True when `value` parses and lies within the last `days` days of `now`,
// boundary included (future timestamps count as recent).
export function isWithinDays(value: string | null | undefined, days: number, now: number): boolean {
  const t = timeOrNaN(value);
  return !isNaN(t) && t >= now - days * MS_PER_DAY;
}

// isWithinDays for lastChanged: also ignores implausibly future values.
function changedWithinDays(lastChanged: string | null | undefined, days: number, now: number): boolean {
  const t = changedTimeOrNaN(lastChanged, now);
  return !isNaN(t) && t >= now - days * MS_PER_DAY;
}

// "This week" for Viikon kärki (HeroSplit): the item was first seen
// (createdon) or its content last changed within the window. Items without
// firstSeen (old backend, bookmark snapshots) fall back to publishedDate.
export function isThisWeek(
  item: Pick<ParsedItem, 'publishedDate' | 'firstSeen' | 'lastChanged'>,
  now: number,
  windowDays: number,
): boolean {
  const arrived = item.firstSeen ?? item.publishedDate;
  return isWithinDays(arrived, windowDays, now) || changedWithinDays(item.lastChanged, windowDays, now);
}

// The card's "Päivitetty" marker: lastChanged within CHANGED_MARKER_DAYS
// (boundary included); a lastChanged beyond now + 1 day is ignored.
export function isRecentlyChanged(
  item: Pick<ParsedItem, 'lastChanged'>,
  now: number = Date.now(),
  days: number = CHANGED_MARKER_DAYS,
): boolean {
  return changedWithinDays(item.lastChanged, days, now);
}
